import { useState, useEffect, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';
import PostCard, { CommunityPost } from '@/pages/comunidad/components/PostCard';
import NewPostModal from '@/pages/comunidad/components/NewPostModal';

const FORUM_CATEGORIES = [
  { id: 'all',      label: 'Todo',         icon: 'ri-layout-grid-line' },
  { id: 'futbol',   label: 'Fútbol',       icon: 'ri-football-line' },
  { id: 'tenis',    label: 'Tenis',        icon: 'ri-ping-pong-line' },
  { id: 'basket',   label: 'Baloncesto',   icon: 'ri-basketball-line' },
  { id: 'analisis', label: 'Análisis',     icon: 'ri-bar-chart-line' },
];

export default function ComunidadPage() {
  const { isRegistered, supabaseUser, openLoginModal, isPremium, openPaywallModal } = useAuth();
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [showNewPost, setShowNewPost] = useState(false);

  // Category counts derived from loaded posts
  const categoryCounts = FORUM_CATEGORIES.reduce<Record<string, number>>((acc, cat) => {
    acc[cat.id] = cat.id === 'all'
      ? posts.length
      : posts.filter((p) => p.category === cat.id).length;
    return acc;
  }, {});

  const fetchPosts = useCallback(async () => {
    setLoading(true);

    const query = supabase
      .from('posts')
      .select('*, profiles!posts_profile_fkey(username, plan)')
      .order('created_at', { ascending: false })
      .limit(100);

    const { data, error } = await query;

    if (error || !data) {
      setLoading(false);
      return;
    }

    // Fetch which posts current user has liked
    let likedPostIds: Set<string> = new Set();
    if (supabaseUser) {
      const { data: likes } = await supabase
        .from('post_likes')
        .select('post_id')
        .eq('user_id', supabaseUser.id);
      likedPostIds = new Set((likes ?? []).map((l: { post_id: string }) => l.post_id));
    }

    const enriched = (data as CommunityPost[]).map((p) => ({
      ...p,
      userLiked: likedPostIds.has(p.id),
    }));

    setPosts(enriched);
    setLoading(false);
  }, [supabaseUser]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleLikeToggle = (postId: string, liked: boolean) => {
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, userLiked: liked, likes_count: liked ? p.likes_count + 1 : Math.max(p.likes_count - 1, 0) }
          : p
      )
    );
  };

  const handleNewPostClick = () => {
    if (!isRegistered) { openLoginModal(); return; }
    setShowNewPost(true);
  };

  // Filter + sort
  const filtered = posts
    .filter((p) => activeCategory === 'all' || p.category === activeCategory)
    .sort((a, b) => {
      if (sortBy === 'recent') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      if (sortBy === 'trending') return b.likes_count - a.likes_count;
      if (sortBy === 'picks') return Number(b.is_prediction) - Number(a.is_prediction);
      return 0;
    });

  return (
    <div className="bg-[#0d0f14] min-h-screen">
      <Navbar />
      <div className="pt-16">
        {/* Header */}
        <div className="bg-[#151719] border-b border-[#2a2d3a]">
          <div className="max-w-[1440px] mx-auto px-6 py-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <p className="text-[#00e87a] text-xs font-bold tracking-widest uppercase mb-2">Comunidad</p>
                <h1 className="text-white text-4xl font-black uppercase">Foro Deportivo</h1>
                <p className="text-[#a8b2c1] text-sm mt-2">Análisis, picks y debate entre apostadores verificados</p>
              </div>
              <button
                onClick={handleNewPostClick}
                className="flex items-center gap-2 px-5 py-3 bg-[#00e87a] text-black text-sm font-bold rounded-xl hover:bg-[#00d070] transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-add-line text-base"></i>
                </div>
                Nuevo Post
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-4">
                <p className="text-[#6b7280] text-xs font-semibold uppercase tracking-wide mb-3">Categorías</p>
                <div className="space-y-1">
                  {FORUM_CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                        activeCategory === cat.id
                          ? 'bg-[#00e87a]/15 text-[#00e87a]'
                          : 'text-[#a8b2c1] hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${cat.icon} text-sm`}></i>
                        </div>
                        {cat.label}
                      </span>
                      <span className="text-xs text-[#6b7280]">{categoryCounts[cat.id] ?? 0}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-[#00e87a]/5 border border-[#00e87a]/20 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 flex items-center justify-center text-[#00e87a]">
                    <i className="ri-shield-check-line text-sm"></i>
                  </div>
                  <p className="text-[#00e87a] text-xs font-bold uppercase">Picks Verificados</p>
                </div>
                <p className="text-[#a8b2c1] text-xs leading-relaxed">
                  Todos los picks públicos quedan registrados. El resultado real actualiza automáticamente el ROI del tipster.
                </p>
              </div>

              {/* Premium upsell */}
              {isRegistered && !isPremium && (
                <div className="bg-[#ffd700]/5 border border-[#ffd700]/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 flex items-center justify-center text-[#ffd700]">
                      <i className="ri-vip-crown-line text-sm"></i>
                    </div>
                    <p className="text-[#ffd700] text-xs font-bold uppercase">Premium</p>
                  </div>
                  <p className="text-[#a8b2c1] text-xs leading-relaxed mb-3">
                    Añade stake por pick, análisis avanzado y destaca tus picks en el feed con el badge Pro.
                  </p>
                  <button
                    onClick={() => openPaywallModal('premium')}
                    className="w-full py-2 bg-[#ffd700]/15 border border-[#ffd700]/30 text-[#ffd700] text-xs font-bold rounded-lg cursor-pointer hover:bg-[#ffd700]/25 transition-all whitespace-nowrap"
                  >
                    Desbloquear Premium
                  </button>
                </div>
              )}

              {!isRegistered && (
                <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-4 text-center">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00e87a]/10 mx-auto mb-3">
                    <i className="ri-user-add-line text-[#00e87a] text-xl"></i>
                  </div>
                  <p className="text-white font-bold text-sm mb-1">Únete a la comunidad</p>
                  <p className="text-[#6b7280] text-xs mb-3">Regístrate gratis para publicar picks y debates</p>
                  <button
                    onClick={openLoginModal}
                    className="w-full py-2 bg-[#00e87a] text-black text-xs font-bold rounded-lg cursor-pointer whitespace-nowrap"
                  >
                    Iniciar sesión
                  </button>
                </div>
              )}
            </div>

            {/* Posts main area */}
            <div className="lg:col-span-3 space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1 bg-[#151719] border border-[#2a2d3a] rounded-xl p-1">
                  {[
                    { value: 'recent',   label: 'Recientes' },
                    { value: 'trending', label: 'Más votados' },
                    { value: 'picks',    label: 'Solo Picks' },
                  ].map((s) => (
                    <button
                      key={s.value}
                      onClick={() => setSortBy(s.value)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        sortBy === s.value ? 'bg-[#00e87a]/15 text-[#00e87a]' : 'text-[#6b7280] hover:text-white'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <span className="text-[#6b7280] text-sm">{filtered.length} posts</span>
              </div>

              {loading && (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-5 animate-pulse">
                      <div className="flex gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#2a2d3a] flex-shrink-0"></div>
                        <div className="flex-1 space-y-3">
                          <div className="h-3 bg-[#2a2d3a] rounded w-1/3"></div>
                          <div className="h-4 bg-[#2a2d3a] rounded w-3/4"></div>
                          <div className="h-3 bg-[#2a2d3a] rounded w-full"></div>
                          <div className="h-3 bg-[#2a2d3a] rounded w-2/3"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!loading && filtered.length === 0 && (
                <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-14 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#00e87a]/10 mx-auto mb-4">
                    <i className="ri-chat-3-line text-[#00e87a] text-2xl"></i>
                  </div>
                  <p className="text-white font-bold text-lg mb-2">
                    {activeCategory === 'all' ? 'Sé el primero en publicar' : 'Sin posts en esta categoría'}
                  </p>
                  <p className="text-[#6b7280] text-sm mb-5">
                    La comunidad está empezando. Comparte tu análisis o pick y lidera el debate.
                  </p>
                  <button
                    onClick={handleNewPostClick}
                    className="px-5 py-2.5 bg-[#00e87a] text-black text-sm font-bold rounded-xl cursor-pointer whitespace-nowrap"
                  >
                    Crear el primer post
                  </button>
                </div>
              )}

              {!loading && filtered.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  onLikeToggle={handleLikeToggle}
                  onDelete={(postId) => setPosts((prev) => prev.filter((p) => p.id !== postId))}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {showNewPost && (
        <NewPostModal
          onClose={() => setShowNewPost(false)}
          onPostCreated={fetchPosts}
        />
      )}

      <Footer />
    </div>
  );
}
